import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FAVICON_URL } from '@/lib/seo';

export default function Article() {
  const { t } = useTranslation('article');

  const benefits = t('benefits.items', { returnObjects: true }) as string[];
  const categoryItems = t('categories.items', { returnObjects: true }) as string[];
  const relatedItems = t('related.items', { returnObjects: true }) as string[];
  const tableRows = t('comparison.table', { returnObjects: true }) as Array<{
    option: string;
    bestFor: string;
    advantage: string;
    consideration: string;
  }>;

  const schemaFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('faq.q1'), acceptedAnswer: { '@type': 'Answer', text: t('faq.a1') } },
      { '@type': 'Question', name: t('faq.q2'), acceptedAnswer: { '@type': 'Answer', text: t('faq.a2') } },
      { '@type': 'Question', name: t('faq.q3'), acceptedAnswer: { '@type': 'Answer', text: t('faq.a3') } },
      { '@type': 'Question', name: t('faq.q4'), acceptedAnswer: { '@type': 'Answer', text: t('faq.a4') } },
      { '@type': 'Question', name: t('faq.q5'), acceptedAnswer: { '@type': 'Answer', text: t('faq.a5') } },
    ],
  };

  const schemaArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('meta.title'),
    description: t('meta.description'),
    datePublished: '2026-08-01',
    dateModified: '2026-08-26',
    author: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet Resource',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(schemaArticle)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaFAQ)}</script>

      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/70 mt-16">
        <div className="px-4 md:px-6 py-3">
          <nav className="max-w-4xl mx-auto">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-foreground-800 cursor-pointer">{t('backToHome')}</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs" /></li>
              <li className="text-foreground-700 font-medium">{t('hero.title')}</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white border-b border-background-200/70">
        <div className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block text-xs font-semibold tracking-widest text-primary-600 uppercase mb-4">
              GUIDE 2026
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              {t('hero.subtitle')}
            </p>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {t('lastUpdated')}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                8 min read
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-10 md:py-16">
        <div className="px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Intro */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('intro.title')}
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                {t('intro.content')}
              </p>
            </div>

            {/* What Is */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('whatIs.title')}
              </h2>
              <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8 mb-6">
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                  {t('whatIs.content')}
                </p>
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('howItWorks.title')}
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                {t('howItWorks.content')}
              </p>
            </div>

            {/* Benefits */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('benefits.title')}
              </h2>
              <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8">
                <ul className="space-y-3">
                  {benefits.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground-600 text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('comparison.title')}
              </h2>
              <p className="text-foreground-600 text-sm mb-4">{t('comparison.description')}</p>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-xl border border-background-200/70 text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Option</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Best For</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Advantage</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Consideration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, idx) => (
                      <tr key={idx} className="border-b border-background-200/70 last:border-0">
                        <td className="px-4 py-3 font-medium text-foreground-900">{row.option}</td>
                        <td className="px-4 py-3 text-foreground-600">{row.bestFor}</td>
                        <td className="px-4 py-3 text-foreground-600">{row.advantage}</td>
                        <td className="px-4 py-3 text-foreground-600">{row.consideration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Categories */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('categories.title')}
              </h2>
              <p className="text-foreground-600 mb-4 text-sm md:text-base">{t('categories.content')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {categoryItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-white rounded-lg border border-background-200/70">
                    <i className="ri-arrow-right-line text-primary-500 text-sm" />
                    <span className="text-sm text-foreground-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Safety */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('safety.title')}
              </h2>
              <div className="bg-accent-50 rounded-xl border border-accent-200/50 p-6 md:p-8">
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-accent-600 text-xl mt-0.5 flex-shrink-0" />
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    {t('safety.content')}
                  </p>
                </div>
              </div>
            </div>

            {/* Internal Link to Home */}
            <div className="mb-12 bg-primary-50 rounded-xl border border-primary-200/50 p-6">
              <div className="flex items-start gap-3">
                <i className="ri-home-line text-primary-600 text-xl mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">Voltar para Home</h3>
                  <p className="text-sm text-primary-700 mb-2">
                    Explore mais recursos, guias e informações sobre o CSSBuy Spreadsheet na página inicial.
                  </p>
                  <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                    <i className="ri-arrow-left-line" />
                    Acessar página inicial
                  </Link>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('faq.title')}
              </h2>
              <div className="space-y-4">
                {[
                  { q: 'faq.q1', a: 'faq.a1' },
                  { q: 'faq.q2', a: 'faq.a2' },
                  { q: 'faq.q3', a: 'faq.a3' },
                  { q: 'faq.q4', a: 'faq.a4' },
                  { q: 'faq.q5', a: 'faq.a5' },
                ].map((faq, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-background-200/70 p-5">
                    <h3 className="font-semibold text-foreground-900 mb-2 text-sm md:text-base">
                      {t(faq.q)}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {t(faq.a)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                {t('related.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedItems.map((item, idx) => (
                  <a
                    key={idx}
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 p-4 bg-white rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50 transition-all cursor-pointer"
                  >
                    <i className="ri-article-line text-primary-500" />
                    <span className="text-sm text-foreground-700">{item}</span>
                    <i className="ri-arrow-right-line ml-auto text-foreground-400 text-sm" />
                  </a>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-primary-500 rounded-xl p-8 md:p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Delicate%20abstract%20botanical%20pattern%20in%20soft%20sage%20green%20and%20cream%20tones%20with%20flowing%20organic%20lines%20minimal%20modern%20background%20texture%20clean%20airy%20aesthetic%20no%20objects&width=1200&height=400&seq=article-cta-bg-01&orientation=landscape&nocache=true"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                  {t('cta.title')}
                </h3>
                <p className="text-white/85 text-sm md:text-base mb-6">
                  {t('cta.description')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-white text-primary-600 font-medium px-8 py-3 rounded-lg hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('cta.primary')}
                    <i className="ri-arrow-right-line" />
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-white/15 text-white font-medium px-8 py-3 rounded-lg hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('cta.secondary')}
                    <i className="ri-shopping-cart-line" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Disclaimer */}
      <div className="bg-background-100 border-t border-background-200/70 py-4">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500">
            {t('disclaimer')}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}